import { useEffect, useState } from 'react';
import { ThreadMessage, useAuth } from '@screentone/addon-auth-wrapper';

import { NoSearchResults } from 'components/no-search-results/NoSearchResults';
import { getPageLockThreadId, PageLockProvider } from 'contexts/page-lock/PageLockContext';
import { AvailableFeatureFlags, PageDto, PagesDtoQuery, PageType } from 'data/generated/graphql';
import { OKTA_ENV } from 'features/auth/components/OktaRouter';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useIsAdmin } from 'hooks/roles';
import env from 'utils/env';
import { PageListItem } from './PageListItem';

// eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
const prod = env.OKTA_ENV === OKTA_ENV.PRD;
const DEFAULT_ORDER_IF_NOT_SET = 99999;

const convertBaseRoute = (publicationKey: string, baseRoute: string | undefined, routeKey: string): string => {
  if (baseRoute) {
    if (publicationKey === 'wsjopinion') {
      // Remove '/opinion' part from the base route for Arts & Culture section
      if (routeKey.startsWith('/arts-culture')) {
        return baseRoute.replace('/opinion', '');
      }
    }
  }

  return baseRoute ?? '';
};

const convertRouteKey = (routeKey: string, publicationKey: string, pageType: PageType): string => {
  if (publicationKey === 'barrons') {
    if (routeKey === '/') return routeKey;
    if (pageType === PageType.Section) {
      if (routeKey === '/advisor_center') return '/advisor';
    }
    if (pageType === PageType.Home) {
      // original advisor
      if (routeKey === '/advisor') {
        if (prod) return '/';
        // add a query string for dev advisor
        return '/?ap=y';
      }
      // for test advisor homepages
      const isTestAdvisorDev = routeKey.includes('advisor') && !routeKey.includes('advisor_center');
      return `/?curationPage=${routeKey.replace('/', '')}${isTestAdvisorDev ? '&ap=y' : ''}`;
    }
  }

  return routeKey;
};

export const PagesList = ({
  pagePages,
  initialThreadData
}: {
  pagePages: PagesDtoQuery[];
  initialThreadData: Record<string, ThreadMessage[]>;
}) => {
  const { currentProperty } = useAuth();
  const isAdmin = useIsAdmin();
  const { data: publicationSettings } = usePublicationSettings();
  const [pages, setPages] = useState<PageDto[]>([]);

  useEffect(() => {
    const pagesWithOrder = pagePages.reduce((acc: { page: PageDto; order: number }[], curr: PagesDtoQuery) => {
      const pages = curr.pagesDTO.edges.map((pageEdge) => {
        const { node: page, order } = pageEdge;
        return { page, order: order ?? DEFAULT_ORDER_IF_NOT_SET };
      });
      return acc.concat(pages as { page: PageDto; order: number }[]);
    }, []);

    // Sort pages by order first (ascending), then by name (alphabetically)
    pagesWithOrder.sort((a, b) => a.order - b.order || a.page.metadata.name.localeCompare(b.page.metadata.name));

    const pages = pagesWithOrder.map(({ page }) => page);
    setPages(pages);
  }, [isAdmin, pagePages]);

  if (pagePages.length === 0 || pagePages[0].pagesDTO.edges.length === 0) {
    return <NoSearchResults title="Pages" bodyText="Try a different filter or ask an admin to create a new page." />;
  }

  const slugMappings = publicationSettings?.publicationSetting.slugMappings;

  return (
    <div data-testid="pages-sections-list">
      {pages.map((page) => {
        const baseRoute = convertBaseRoute(
          page.metadata.publicationKey,
          publicationSettings?.publicationSetting.baseRoute,
          page.root.attributes.routeKey.toLowerCase()
        );
        const routeKey = convertRouteKey(
          page.root.attributes.routeKey.toLowerCase(),
          page.metadata.publicationKey,
          page.root.attributes.pageType as PageType
        );
        const [slug] =
          Object.entries(slugMappings ?? {}).find(([, allessehId]) => allessehId === page.metadata.allessehId) ?? [];
        const pageId = slug ?? page.metadata.allessehId;
        const to = currentProperty ? `/${currentProperty}/pages/${pageId}` : `/pages/${pageId}`;
        const threadId = getPageLockThreadId(page.metadata.allessehId);
        const isReady = threadId in initialThreadData;
        return (
          <PageLockProvider
            key={`${page.metadata.allessehId}-${isReady ? 'ready' : 'loading'}`}
            allessehId={page.metadata.allessehId}
            fragments={['publishing']}
            featureFlag={AvailableFeatureFlags.PageLockingEnabled}
            skipInitialFetch
            initialMessages={initialThreadData[threadId]}
          >
            <PageListItem
              page={page}
              to={to}
              baseRoute={baseRoute}
              routeKey={routeKey}
              publicationSettings={publicationSettings}
            />
          </PageLockProvider>
        );
      })}
    </div>
  );
};
